import { Section, Stat, inr, qtyFmt } from "./costingUi";

const PRICE_SRC = { SALES_INVOICES_WEIGHTED_AVG: "avg of sales bills", PRODUCT_MASTER: "Product Master price" };

function SkuCard({ s, perKg }) {
  return (
    <div data-testid={`sku-card-${s.productId}`} className="rounded-lg border border-stone-200 bg-stone-50/50 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-sm font-semibold text-stone-900">{s.productId} · {s.name}</div>
        <span className="text-[11px] text-stone-500">{s.sku}{s.isPrimary ? " · primary recipe product" : " · linked via Product Master"}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
        <Stat label="Retail pack / sale unit" value={s.productUnit ? `per ${s.productUnit}${s.piecesPerPack ? ` (${s.piecesPerPack} pcs)` : ""}` : null} missing="Not recorded" />
        <Stat label="Net qty per pack" value={s.netQtyPerPack} missing="Not recorded in Product Master" />
        <Stat testId={`sku-${s.productId}-packs`} label="Saleable packs / batch" value={s.packsPerBatch != null ? qtyFmt(s.packsPerBatch) : null} missing="Not established" />
        <Stat label="Packaging materials" value={s.packagingBom?.length ? `${s.packagingBom.length} item(s)` : null} missing="None recorded" />
        <Stat label="Ingredient cost / pack" value={inr(s.ingredientCostPerPack, 4)} missing="Not established" />
        <Stat label="Packaging cost / pack" value={inr(s.packagingCostPerPack, 4)} missing="Not established" />
        <Stat testId={`sku-${s.productId}-total`} label="Total cost / pack" value={inr(s.totalCostPerPack, 4)} missing="Not established" strong />
        <Stat label="Cost / kg finished" value={inr(perKg)} missing="Net weight not recorded" />
        <Stat testId={`sku-${s.productId}-price`} label="Selling price" value={inr(s.sellingPrice)} missing="No sales price"
          sub={s.sellingPriceSource ? `${PRICE_SRC[s.sellingPriceSource] || s.sellingPriceSource}${s.invoiceLineCount ? ` · ${s.invoiceLineCount} line${s.invoiceLineCount === 1 ? "" : "s"}` : ""}` : null} />
        <Stat testId={`sku-${s.productId}-margin`} label="Gross margin" value={s.grossMargin != null ? `${inr(s.grossMargin)} (${s.marginPct}%)` : null} missing="Not comparable" />
      </div>
      {s.warnings.length > 0 && (
        <ul className="mt-3 space-y-0.5" data-testid={`sku-${s.productId}-warnings`}>
          {s.warnings.map((w) => <li key={w} className="text-[11px] font-medium text-amber-700">• {w}</li>)}
        </ul>
      )}
    </div>
  );
}

export function SkuCostingSection({ data }) {
  const { skus, perUnit } = data;
  return (
    <Section title="SKU Costing & Packaging" testId="sku-costing-section">
      {skus.length === 0 ? (
        <p className="text-sm text-amber-700" data-testid="sku-none">No product is explicitly linked to this recipe.</p>
      ) : (
        <div className="space-y-3">{skus.map((s) => <SkuCard key={s.productId} s={s} perKg={perUnit.costPerKgFinished} />)}</div>
      )}
      <p className="mt-3 text-[11px] text-stone-500">
        Only products explicitly linked by Product ID / current recipe are shown. Packaging-specific allocation uses recorded packaging data only; nothing is estimated.
      </p>
    </Section>
  );
}
